import React from "react";

import {
  faCalendar,
  faSearch,
  faWarning,
} from "@fortawesome/pro-regular-svg-icons";

import { DisplayDateTime } from "src/base-components/Date";
import {
  PropertiesList,
  PropertiesListItem,
} from "src/base-components/PropertiesList";
import { CaseBlock, CaseBlockProps } from "src/cases/caseView/CaseBlock";
import {
  renderByType,
  useCaseEntityDisplayMap,
} from "src/cases/common/casePropertyRenderers";
import { useGetCasePropertyIcon } from "src/cases/common/useGetCasePropertyIcon";
import { useCase } from "src/cases/queries/cases";
import { CaseEnvironment } from "src/clients/case-management";
import { EmptyState } from "src/design-system/EmptyState";
import { Skeleton } from "src/design-system/Skeleton";
import { Environment } from "src/hooks/useEnvironment";

type CaseDetailsBlockProps = Omit<CaseBlockProps, "children"> & {
  caseId: string | undefined | null;
  caseEnv: CaseEnvironment;
};

export const CaseDetailsBlock: React.FC<CaseDetailsBlockProps> = ({
  caseId,
  caseEnv,
  ...props
}) => {
  const { data: caseData, isLoading, error: caseError } = useCase(caseId);
  const getPropertyIcon = useGetCasePropertyIcon();

  const template = caseData?.data.template;
  const templateVersion = caseData?.data.template_version;

  const env = caseEnv === CaseEnvironment.LIVE ? "live" : "sandbox";

  const { displayMap: entityDisplayMap } = useCaseEntityDisplayMap({
    caseProperties: caseData?.data.case_properties,
    schemaProperties: templateVersion?.case_properties,
    environment: env as Environment,
  });

  if (!caseId) {
    return (
      <CaseBlock {...props}>
        <EmptyState
          dataLoc="case-details-no-case-id"
          description="This block currently has no linked case."
          headline="No case provided"
          icon={faSearch}
          size="compact"
        />
      </CaseBlock>
    );
  }

  if (isLoading) {
    return (
      <CaseBlock {...props}>
        <div className="flex h-full flex-col">
          <div className="mb-3" data-loc="case-details-description-loading">
            <Skeleton height="h-3.5" width="w-full" />
          </div>
          <PropertiesList isLoading={true} items={[]} loadingRows={5} />
        </div>
      </CaseBlock>
    );
  }

  if (!caseData || !templateVersion || !template || caseError) {
    return (
      <CaseBlock {...props}>
        <EmptyState
          dataLoc="case-details-no-case-data"
          description="Unable to load case data"
          headline="Error loading case"
          icon={faWarning}
          size="compact"
          variant="error"
        />
      </CaseBlock>
    );
  }

  const caseProperties = caseData.data.case_properties || {};
  const schemaProperties = templateVersion.case_properties;

  const propertyItems: PropertiesListItem[] = Object.entries(caseProperties)
    .filter(([key]) => schemaProperties[key]) // Only show properties that exist in schema
    .map(([key, value]) => {
      const schema = schemaProperties[key];
      const icon = getPropertyIcon(schema);

      return {
        key,
        label: schema.display_name || key,
        icon,
        value: renderByType({
          value,
          schema,
          caseProperties,
          key,
          icon,
          caseEnv,
          entityDisplayMap,
        }),
      };
    });

  const createdAt = caseData.data.created_at;
  if (createdAt) {
    propertyItems.push({
      key: "created_at",
      label: "Case Created At",
      icon: faCalendar,
      value: (
        <span className="font-inter-normal-12px">
          <DisplayDateTime value={createdAt} />
        </span>
      ),
    });
  }

  return (
    <CaseBlock {...props}>
      <div className="flex h-full flex-col">
        <PropertiesList dataLocPrefix="case-details" items={propertyItems} />
      </div>
    </CaseBlock>
  );
};
